import React from "react";
import {
  Link,
  LinkProps,
  NavLink,
  NavLinkProps,
  To,
  useNavigate,
  NavigateOptions,
} from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { GEOCATALOGNAME_PARAM, PRPV_PARAM } from "utils/constants";

// Parameters that must always be carried across navigation. All other current
// query params are passed through as well so deep-link state (collection,
// search, sort, active edit, etc.) is preserved.
const PRESERVED_PARAMS = [GEOCATALOGNAME_PARAM, PRPV_PARAM];

const filterPreservedParams = (searchParams: URLSearchParams): URLSearchParams => {
  // Pass through all existing params, then ensure must-keep params are present.
  const filtered = new URLSearchParams(searchParams);
  PRESERVED_PARAMS.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      filtered.set(key, value);
    }
  });
  return filtered;
};

/**
 * Constructs the full path with preserved search parameters
 * @param to - The target path (string or object)
 * @param searchParams - Current URL search parameters
 * @returns The path with preserved query parameters merged in
 */
const getFullPath = (to: To, searchParams: URLSearchParams): To => {
  const filteredParams = filterPreservedParams(searchParams);
  const paramsString = filteredParams.toString();

  if (typeof to === "string") {
    if (!paramsString) return to;

    // Check if the path already has query params
    const separator = to.includes("?") ? "&" : "?";
    return `${to}${separator}${paramsString}`;
  }

  // If 'to' is an object, merge the search params
  if (typeof to === "object") {
    const existingSearch = to.search || "";
    const separator = existingSearch.includes("?") ? "&" : "?";
    return {
      ...to,
      search: paramsString
        ? `${existingSearch}${separator}${paramsString}`
        : existingSearch,
    };
  }

  return to;
};

export const LinkWithParams: React.FC<LinkProps> = ({ to, ...props }) => {
  const [searchParams] = useSearchParams();
  return <Link to={getFullPath(to, searchParams)} {...props} />;
};

export const NavLinkWithParams: React.FC<NavLinkProps> = ({ to, ...props }) => {
  const [searchParams] = useSearchParams();
  return <NavLink to={getFullPath(to, searchParams)} {...props} />;
};

/**
 * Custom hook that wraps useNavigate and preserves current URL search parameters
 * Usage: const navigate = useNavigateWithParams();
 *        navigate('/path') - preserves current params
 *        navigate('/path', { preserveParams: false }) - doesn't preserve params
 */
export const useNavigateWithParams = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return React.useCallback(
    (to: To | number, options?: NavigateOptions & { preserveParams?: boolean }) => {
      // If navigating with a number (back/forward), use default behavior
      if (typeof to === "number") {
        navigate(to);
        return;
      }

      // Default to preserving params unless explicitly disabled
      const shouldPreserveParams = options?.preserveParams !== false;

      if (!shouldPreserveParams) {
        navigate(to, options);
        return;
      }

      const filteredParams = filterPreservedParams(searchParams);
      const paramsString = filteredParams.toString();

      if (typeof to === "string") {
        if (!paramsString) {
          navigate(to, options);
          return;
        }

        // Handle hash-only navigation (preserve search params in current path)
        if (to.startsWith("#")) {
          navigate({ search: `?${paramsString}`, hash: to }, options);
          return;
        }

        const separator = to.includes("?") ? "&" : "?";
        navigate(`${to}${separator}${paramsString}`, options);
      } else {
        // If 'to' is an object, merge the search params
        const existingSearch = to.search || "";
        const hasQuery = existingSearch.startsWith("?") && existingSearch.length > 1;
        const separator = hasQuery ? "&" : "?";
        const newSearch = paramsString
          ? `${existingSearch}${separator}${paramsString}`
          : existingSearch;

        navigate(
          {
            ...to,
            search: newSearch,
          },
          options
        );
      }
    },
    [navigate, searchParams]
  );
};
